import { useEffect, useRef } from 'react';

export const useControlledTimeout = () => {
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const timeRemainingRef = useRef(0);
    const isPausedRef = useRef(false);
    const startTimeRef = useRef(0);
    const durationRef = useRef(0);
    const callbackRef = useRef<() => void>();

    const start = (cb: () => void, ms: number) => {
        clearTimeout(timeoutRef.current);
        callbackRef.current = cb;
        durationRef.current = ms;
        startTimeRef.current = Date.now();
        isPausedRef.current = false;
        timeRemainingRef.current = durationRef.current;

        timeoutRef.current = setTimeout(() => {
            cb();
            timeoutRef.current = undefined;
        }, durationRef.current);
    };

    const pause = () => {
        if (timeoutRef.current && !isPausedRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = undefined;

            const elapsed = Date.now() - startTimeRef.current;
            timeRemainingRef.current = durationRef.current - elapsed;
            isPausedRef.current = true;
        }
    };

    const resume = () => {
        if (isPausedRef.current && timeRemainingRef.current > 0 && callbackRef.current) {
            startTimeRef.current = Date.now();
            timeoutRef.current = setTimeout(() => {
                callbackRef.current?.();
                timeoutRef.current = undefined;
            }, timeRemainingRef.current);
            isPausedRef.current = false;
        }
    };

    const clear = () => {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
        isPausedRef.current = false;
        timeRemainingRef.current = 0;
        startTimeRef.current = 0;
        durationRef.current = 0;
        callbackRef.current = undefined;
    };

    useEffect(() => clear, [])

    return { start, pause, resume, clear };
};
